import React, { useEffect, useState, createContext, useContext } from 'react';
interface User {
  name: string;
  email: string;
}
interface Profile {
  province?: string;
  completingFor?: string;
  children?: string;
  property?: string;
  previousCheck?: string;
}
interface Preferences {
  startWith?: string;
  motivations?: string[];
}
interface Business {
  hasBusiness?: boolean;
  name?: string;
  type?: string;
  province?: string;
  employees?: string;
  industry?: string;
  hasWebsite?: string;
  usesContracts?: string;
  hasLegalSupport?: string;
  concerns?: string[];
  businessAge?: string;
}
interface AuthContextType {
  isAuthenticated: boolean;
  isOnboarded: boolean;
  user: User | null;
  profile: Profile | null;
  preferences: Preferences | null;
  business: Business | null;
  login: (email: string) => void;
  signup: (name: string, email: string) => void;
  logout: () => void;
  updateProfile: (data: Partial<Profile>) => void;
  updatePreferences: (data: Partial<Preferences>) => void;
  updateBusiness: (data: Partial<Business>) => void;
  completeOnboarding: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    const storedAuth = localStorage.getItem('lawvo_auth');
    if (storedAuth) {
      const parsed = JSON.parse(storedAuth);
      setIsAuthenticated(parsed.isAuthenticated);
      setIsOnboarded(parsed.isOnboarded);
      setUser(parsed.user);
      setProfile(parsed.profile);
      setPreferences(parsed.preferences || null);
      setBusiness(parsed.business);
    }
    setIsInitialized(true);
  }, []);
  const saveState = (state: any) => {
    localStorage.setItem('lawvo_auth', JSON.stringify(state));
  };
  const login = (email: string) => {
    const newState = {
      isAuthenticated: true,
      isOnboarded,
      user: {
        name: 'User',
        email
      },
      profile,
      preferences,
      business
    };
    setIsAuthenticated(true);
    setUser({
      name: 'User',
      email
    });
    saveState(newState);
  };
  const signup = (name: string, email: string) => {
    const newState = {
      isAuthenticated: true,
      isOnboarded: false,
      user: {
        name,
        email
      },
      profile: null,
      preferences: null,
      business: null
    };
    setIsAuthenticated(true);
    setIsOnboarded(false);
    setUser({
      name,
      email
    });
    setProfile(null);
    setPreferences(null);
    setBusiness(null);
    saveState(newState);
  };
  const logout = () => {
    setIsAuthenticated(false);
    setIsOnboarded(false);
    setUser(null);
    setProfile(null);
    setPreferences(null);
    setBusiness(null);
    localStorage.removeItem('lawvo_auth');
  };
  const updateProfile = (data: Partial<Profile>) => {
    setProfile(prev => {
      const newProfile = {
        ...prev,
        ...data
      };
      saveState({
        isAuthenticated,
        isOnboarded,
        user,
        profile: newProfile,
        preferences,
        business
      });
      return newProfile;
    });
  };
  const updatePreferences = (data: Partial<Preferences>) => {
    setPreferences(prev => {
      const newPreferences = {
        ...prev,
        ...data
      };
      saveState({
        isAuthenticated,
        isOnboarded,
        user,
        profile,
        preferences: newPreferences,
        business
      });
      return newPreferences;
    });
  };
  const updateBusiness = (data: Partial<Business>) => {
    setBusiness(prev => {
      const newBusiness = {
        ...prev,
        ...data
      };
      saveState({
        isAuthenticated,
        isOnboarded,
        user,
        profile,
        preferences,
        business: newBusiness
      });
      return newBusiness;
    });
  };
  const completeOnboarding = () => {
    setIsOnboarded(true);
    saveState({
      isAuthenticated,
      isOnboarded: true,
      user,
      profile,
      preferences,
      business
    });
  };
  if (!isInitialized) return null;
  return <AuthContext.Provider value={{
    isAuthenticated,
    isOnboarded,
    user,
    profile,
    preferences,
    business,
    login,
    signup,
    logout,
    updateProfile,
    updatePreferences,
    updateBusiness,
    completeOnboarding
  }} data-id="element-277">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}